import React from "react";
import { PERMISSIONS_MAP, Permission, getApiPermissionLabel } from "../../api/zekay/permissions";
import type { PermissionKey, ApiPermissionKey } from "../../api/zekay/permissions";

export { Permission, getApiPermissionLabel as getBackendPermissionLabel };
export type { PermissionKey };
export type { ApiPermissionKey as BackendPermissionKey };

interface PermissionsContextType {
  hasPermission: (key: PermissionKey) => boolean;
  hasAnyPermission: (keys: PermissionKey[]) => boolean;
  hasAllPermissions: (keys: PermissionKey[]) => boolean;
  getUserPermissions: () => PermissionKey[];
  hasBackendPermission: (key: ApiPermissionKey) => boolean;
}

export const PermissionsContext = React.createContext<PermissionsContextType>(null!);

interface PermissionsProviderProps {
  children: React.ReactNode;
  userPermissions?: ApiPermissionKey[];
}

export function PermissionsProvider({ children, userPermissions }: PermissionsProviderProps) {
  const hasPermission = (key: PermissionKey): boolean => {
    if (!userPermissions) return false;
    const perm = PERMISSIONS_MAP[key];
    if (!perm) return false;
    if (userPermissions.includes("admin")) return true;
    return userPermissions.includes(perm.apiKey);
  };

  const hasAnyPermission = (keys: PermissionKey[]): boolean => keys.some((k) => hasPermission(k));

  const hasAllPermissions = (keys: PermissionKey[]): boolean => keys.every((k) => hasPermission(k));

  const getUserPermissions = (): PermissionKey[] => {
    if (!userPermissions) return [];
    return (Object.keys(PERMISSIONS_MAP) as PermissionKey[]).filter((k) =>
      userPermissions.includes(PERMISSIONS_MAP[k].apiKey),
    );
  };

  const hasBackendPermission = (key: ApiPermissionKey): boolean => {
    if (!userPermissions) return false;
    if (userPermissions.includes("admin")) return true;
    return userPermissions.includes(key);
  };

  return (
    <PermissionsContext.Provider
      value={{
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        getUserPermissions,
        hasBackendPermission,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = React.useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
}
